<template><div><code-well-header>
  <dt-icon-user-plus />
</code-well-header>
<p class="d-docsite--paragraph">Check out our complete icon collection in the <RouteLink to="/design/icons/#icon-catalog" class="d-docsite--link d-link">icon catalog</RouteLink>.</p>
<h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Here is an example that demonstrates how you can use the icon component in your project:</p>
<h3 id="vue-2" tabindex="-1" class="d-docsite--header-3">Vue 2 <a class="header-anchor d-link d-docsite--link d-link" href="#vue-2" aria-hidden="true">#</a></h3>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtIconUserPlus <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone-icons/vue2'</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>user<span class="token operator">-</span>plus size<span class="token operator">=</span><span class="token string">"500"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h3 id="vue-3" tabindex="-1" class="d-docsite--header-3">Vue 3 <a class="header-anchor d-link d-docsite--link d-link" href="#vue-3" aria-hidden="true">#</a></h3>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtIconUserPlus <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone-icons/vue3'</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>user<span class="token operator">-</span>plus size<span class="token operator">=</span><span class="token string">"500"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h3 id="without-tree-shaking-deprecated" tabindex="-1" class="d-docsite--header-3">Without Tree Shaking (Deprecated) <a class="header-anchor d-link d-docsite--link d-link" href="#without-tree-shaking-deprecated" aria-hidden="true">#</a></h3>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">import</span> <span class="token punctuation">{</span> DtIcon <span class="token punctuation">}</span> <span class="token keyword">from</span> <span class="token string">'@dialpad/dialtone'</span></span>
<span class="line"></span>
<span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon name<span class="token operator">=</span><span class="token string">"user-plus"</span> size<span class="token operator">=</span><span class="token string">"500"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div><h2 id="changing-sizes" tabindex="-1" class="d-docsite--header-2">Changing Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#changing-sizes" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">Adjust the size using the <code v-pre>size</code> prop. Note that sizes 600, 700, and 800 are exclusively for devices.</p>
<div class="d-gc2">
<code-well-header>
  <div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols2 d-w100p">
    <div class="d-fl-center">
      <dt-icon-settings :size="selectedSize" />
    </div>
    <dt-select-menu label="Size" :options="sizeValues" @change="changeIconSize" />
  </div>
</code-well-header>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>settings size<span class="token operator">=</span><span class="token string">"500"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<h2 id="changing-color" tabindex="-1" class="d-docsite--header-2">Changing Color <a class="header-anchor d-link d-docsite--link d-link" href="#changing-color" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">The icon's color inherits from the parent's foreground color.</p>
<div class="d-gc2">
<code-well-header>
<div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols2 d-w100p">
  <div class="d-fl-center">
    <dt-stack :class="selectedColor" direction="row" as="div" gap="300">
      <dt-icon-settings size="300" />
      <p>Settings</p>
    </dt-stack>
  </div>
  <dt-select-menu label="Color" :options="iconColors" @change="changeIconColor" />
</div>
</code-well-header>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>stack <span class="token keyword">class</span><span class="token operator">=</span><span class="token string">"d-fc-success"</span><span class="token operator">></span></span>
<span class="line">  <span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>settings size<span class="token operator">=</span><span class="token string">"300"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line">  <span class="token operator">&lt;</span>p<span class="token operator">></span>Settings<span class="token operator">&lt;</span><span class="token operator">/</span>p<span class="token operator">></span></span>
<span class="line"><span class="token operator">&lt;</span><span class="token operator">/</span>dt<span class="token operator">-</span>stack<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">When setting the color of an icon take these into considaration:</p>
<div class="d-gc1">
<div style="background: var(--dt-color-purple-100)" class="d-p16 d-hmn164 d-bar8 d-d-flex d-ai-center">
<dt-stack direction="row" as="section" gap="100" class="d-bgc-primary d-bc-default d-bar32 d-py8 d-px16 d-w100p">
<dt-stack direction="row" as="section" gap="300" class="d-fl1">
<dt-icon name="headphones" size="300" ariaLabel="Headphones icon" />
<p class="d-body--md d-truncate d-w100p d-wmx102">Ai Contact Center</p>
</dt-stack>
<dt-stack direction="row" as="section" gap="300">
<dt-icon class="d-fc-success" name="bell" size="200" ariaLabel="Bell Icon" />
<p class="d-fc-success d-body--sm">Available</p>
</dt-stack>
</dt-stack>
</div>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Match the icon color with the text color when pairing them.</li>
<li class="d-docsite--list-element">All icons are monochrome.</li>
</ul>
</div>
<div class="d-gc1">
<div class="d-bgc-critical-subtle-opaque d-p16 d-hmn164 d-bar8 d-d-flex d-ai-center">
<dt-stack direction="row" as="section" gap="100" class="d-bgc-primary d-bc-default d-bar32 d-py8 d-px16 d-w100p">
<dt-stack direction="row" as="section" gap="300" class="d-fl1">
<dt-icon name="headphones" size="300" ariaLabel="Headphones icon" />
<p class="d-body--md d-truncate d-w100p d-wmx102">Ai Contact Center</p>
</dt-stack>
<dt-stack direction="row" as="section" gap="300">
<dt-icon class="d-fc-critical" name="bell" size="200" ariaLabel="Bell Icon" />
<p class="d-fc-success d-body--sm">Available</p>
</dt-stack>
</dt-stack>
</div>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Don’t use different colors for text and icons.</li>
<li class="d-docsite--list-element">Don’t use more than one color within an icon.</li>
</ul>
</div>
</div>
<h2 id="icon-and-text-alignment" tabindex="-1" class="d-docsite--header-2">Icon and Text Alignment <a class="header-anchor d-link d-docsite--link d-link" href="#icon-and-text-alignment" aria-hidden="true">#</a></h2>
<div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols3">
<p class="d-docsite--paragraph">We encourage utilizing the <RouteLink to="/components/stack.html" class="d-docsite--link d-link">Stack component</RouteLink> for aligning elements both horizontally and vertically.</p>
<div class="d-gc2">
<code-well-header>
  <div class="d-d-grid d-g24 d-g-cols1 md:d-g-cols2 d-w100p">
    <div class="d-fl-center">
      <dt-stack :direction="selectedDirection" class="d-fl-center" gap="300">
      <dt-icon-settings size="300" />
      <p>Settings</p>
      </dt-stack>
    </div>
    <dt-select-menu label="Direction" :options="stackDirection" @change="changeDirection" />
  </div>
</code-well-header>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token operator">&lt;</span>dt<span class="token operator">-</span>stack direction<span class="token operator">=</span><span class="token string">"row"</span> <span class="token keyword">class</span><span class="token operator">=</span><span class="token string">"d-fl-center"</span> gap<span class="token operator">=</span><span class="token string">"300"</span><span class="token operator">></span></span>
<span class="line">  <span class="token operator">&lt;</span>dt<span class="token operator">-</span>icon<span class="token operator">-</span>settings size<span class="token operator">=</span><span class="token string">"300"</span> <span class="token operator">/</span><span class="token operator">></span></span>
<span class="line">  <span class="token operator">&lt;</span>p<span class="token operator">></span>Settings<span class="token operator">&lt;</span><span class="token operator">/</span>p<span class="token operator">></span></span>
<span class="line"><span class="token operator">&lt;</span><span class="token operator">/</span>dt<span class="token operator">-</span>stack<span class="token operator">></span></span>
<span class="line"></span></code></pre>
</div></div>
</div>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">If the icon serves a purpose beyond its visual representation, provide a clear description in the <code v-pre>aria-label</code> prop. This ensures all users understand its function, regardless of how they interact with it, e.g: <code v-pre>&lt;dt-icon-settings aria-label=&quot;Edit your profile&quot; /&gt;</code></p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">Icons contrast guidelines are the same as Typography.</p>
</li>
<li class="d-docsite--list-element">
<p class="d-docsite--paragraph">Avoid using icons as clickable elements; instead, use the <RouteLink to="/components/button.html#icon-only" class="d-docsite--link d-link">Icon Button</RouteLink> for interactive actions.</p>
</li>
</ul>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Dialtone provides eight sizes for icons. Each of the sizes represents the width and a height the icon is going to have:</p>
<div class="d-bar8 d-ba d-bc-subtle">
  <table class="d-table dialtone-doc-table">
    <thead>
      <tr>
        <th scope="col" colspan="2">Size</th>
        <th scope="col">Dimensions</th>
        <th scope="col">Class</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="{size, width_height, className } in sizes">
        <th scope="row">{{ size }}</th>
        <td class="d-ta-center">
          <dt-icon name="inbox" :size="size" />
        </td>
        <td class="d-code--sm d-docsite-code">{{ width_height }}</td>
        <td class="d-code--sm d-docsite-code">.{{ className }}</td>
      </tr>
    </tbody>
  </table>
</div>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="icon" /></div></template>


<script setup>
  import { ref } from 'vue';
  import sizes from '@data/icons-sizes.json';
  import { DtIconUserPlus, DtIconSettings } from '@dialpad/dialtone-icons/vue3';

  const sizeValues = sizes.map(item => ({ value: item.size, label: item.size }));

  const iconListOptions = [
    { value: 'user-plus', label: 'User Plus' },
    { value: 'flame', label: 'Flame' },
    { value: 'heart', label: 'Heart' },
    { value: 'credit-card', label: 'Credit Card' }
  ];

  const iconColors = [
    { value: 'd-fc-success', label: 'd-fc-success' },
    { value: 'd-fc-critical', label: 'd-fc-critical' },
    { value: 'd-fc-primary', label: 'd-fc-primary' },
  ];

  const stackDirection = [
    { value: 'row', label: 'row' },
    { value: 'column', label: 'column'}
  ];

  const selectedIcon = ref('settings');
  const selectedSize = ref(500);
  const selectedColor = ref('d-fc-success');
  const selectedDirection = ref('row');

  const changeIcon = (newIcon) => {
    selectedIcon.value = newIcon;
  };

  const changeIconColor = (newColor) => {
    selectedColor.value = newColor;
  };

  const changeIconSize = (newSize) => {
    selectedSize.value = newSize;
  };

  const changeDirection = (newDirection) => {
    selectedDirection.value = newDirection;
  };
</script>